import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, computed, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { AppSummaryPanelComponent, SummaryPanelRow } from "../../../../Common/Components/SummaryPanel/SummaryPanelComponent";
import { RequestApi } from "../../../Request/Api/RequestApi";
import { RequestEntity } from "../../../Request/Dto/RequestModel";
import { ShipmentEffects } from "../../State/ShipmentEffects";
import { ShipmentCreateStage, ShipmentWarehouseGroup } from "../../Dto/ShipmentPageModel";
import { PartnerApi } from "../../../Partner/Api/PartnerApi";
import { Partner } from "../../../Partner/Dto/PartnerModel";
import { TeamLeaderApi } from "../../../TeamLeader/Api/TeamLeaderApi";
import { TeamLeaderListItem } from "../../../TeamLeader/Dto/TeamLeaderModel";
import {
  buildShipmentDestinationSnapshot,
  createEmptyDestinationSection,
  createEmptyShipmentAddress,
  createSeededDestinationSection,
  formatShipmentAddress,
  isShipmentAddressEmpty,
} from "../Shared/ShipmentDestinationForm";

@Component({
  selector: "app-shipment-create",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AppSummaryPanelComponent,
    LocationSelectorComponent,
    RichTextEditorComponent,
    AutocompleteComponent,
  ],
  templateUrl: "./ShipmentCreatePage.html",
  styleUrl: "./ShipmentCreatePage.scss",
})
export class ShipmentCreatePage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly requestApi = inject(RequestApi);
  private readonly shipmentEffects = inject(ShipmentEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly partnerApi = inject(PartnerApi);
  private readonly teamLeaderApi = inject(TeamLeaderApi);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly requests = signal<RequestEntity[]>([]);
  readonly isReady = signal(false);
  readonly isSaving = signal(false);
  readonly searchTerm = signal("");
  readonly selectedRequestIds = signal<string[]>([]);
  readonly prefilledValidationMessage = signal("");
  readonly warehouseRecoveryGroups = signal<ShipmentWarehouseGroup[]>([]);
  readonly currentStage = signal<ShipmentCreateStage>("select");
  readonly hasTransitStop = signal(false);
  readonly stages: ShipmentCreateStage[] = ["select", "snapshot", "review"];
  private preselectedRequestIds: string[] = [];
  transitPartnerOptions: AutocompleteOption[] = [];
  transitRecipientOptions: AutocompleteOption[] = [];
  selectedTransitPartnerId = "";
  selectedTransitPartner: Partner | null = null;
  selectedTransitRecipientId = "";
  selectedTransitRecipient: TeamLeaderListItem | null = null;
  transitPhoneValue = "";
  transitAddressValue: LocationAddress = this.createEmptyAddress();
  transitRecipientsAvailable: boolean | null = null;
  private readonly transitPartnerMap = new Map<string, Partner>();
  private readonly transitRecipientMap = new Map<string, TeamLeaderListItem>();
  finalPartnerOptions: AutocompleteOption[] = [];
  finalRecipientOptions: AutocompleteOption[] = [];
  selectedFinalPartnerId = "";
  selectedFinalPartner: Partner | null = null;
  selectedFinalRecipientId = "";
  selectedFinalRecipient: TeamLeaderListItem | null = null;
  finalPhoneValue = "";
  finalAddressValue: LocationAddress = this.createEmptyAddress();
  finalRecipientsAvailable: boolean | null = null;
  private readonly finalPartnerMap = new Map<string, Partner>();
  private readonly finalRecipientMap = new Map<string, TeamLeaderListItem>();

  readonly form = this.fb.group({
    note: [""],
  });

  readonly selectedRequests = computed(() => this.requests().filter((request) => this.selectedRequestIds().includes(request.id)));
  readonly isPrefilledLaunch = computed(() => this.preselectedRequestIds.length > 0);
  readonly availableRequests = computed(() =>
    (() => {
      if (this.isPrefilledLaunch()) {
        return this.selectedRequests();
      }
      return this.requests();
    })(),
  );
  readonly filteredRequests = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const selectedWarehouseId = this.selectedWarehouseId();
    return this.availableRequests().filter((request) => {
      if (selectedWarehouseId && request.warehouseId !== selectedWarehouseId && !this.selectedRequestIds().includes(request.id)) {
        return false;
      }
      if (!term) {
        return true;
      }

      const haystack = [
        request.requestNo,
        request.teamLeader?.name,
        request.teamLeader?.phone,
        request.partner?.name,
        request.warehouse?.name,
        ...request.items.map((item) => item.material?.name),
        ...request.items.map((item) => item.material?.sku),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  });
  readonly canInteractWithSelection = computed(() => !this.isPrefilledLaunch() && !this.prefilledValidationMessage());
  readonly isRecoveringMixedWarehousePrefill = computed(() => this.warehouseRecoveryGroups().length > 0);

  readonly totalRequestedQty = computed(() =>
    this.selectedRequests().reduce(
      (sum, request) => sum + request.items.reduce((itemSum, item) => itemSum + Number(item.qty ?? 0), 0),
      0,
    ),
  );
  readonly totalOwners = computed(() => this.selectedRequests().length);
  readonly totalMaterials = computed(() => this.selectedRequests().reduce((sum, request) => sum + request.items.length, 0));

  ngOnInit(): void {
    this.preselectedRequestIds = (this.route.snapshot.queryParamMap.get("requestIds") ?? "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    this.loadConfirmedRequests().catch(() => undefined);
  }

  getStageLabel(stage: ShipmentCreateStage): string {
    if (stage === "select") return "Select Requests";
    if (stage === "snapshot") return "Destination Snapshot";
    return "Review";
  }

  selectedWarehouseId(): string | null {
    return this.selectedRequests()[0]?.warehouseId ?? null;
  }

  selectedWarehouseName(): string {
    return this.selectedRequests()[0]?.warehouse?.name || "-";
  }

  canContinueToSnapshot(): boolean {
    return this.selectedRequestIds().length > 0 && !this.prefilledValidationMessage();
  }

  canContinueToReview(): boolean {
    const finalDestination = this.getFinalDestinationSnapshot();
    if (
      !(
        this.canContinueToSnapshot() &&
        this.selectedFinalPartnerId &&
        this.selectedFinalRecipientId &&
        this.finalPhoneValue.trim() &&
        finalDestination &&
        !this.isAddressEmpty(finalDestination.destinationAddress) &&
        this.finalRecipientsAvailable !== false
      )
    ) {
      return false;
    }

    if (!this.hasTransitStop()) {
      return true;
    }

    const transitSnapshot = this.getTransitSnapshot();
    return Boolean(
      this.selectedTransitPartnerId &&
      this.selectedTransitRecipientId &&
      transitSnapshot?.recipientPhone?.trim() &&
      transitSnapshot?.destinationAddress &&
      !this.isAddressEmpty(transitSnapshot.destinationAddress) &&
      this.transitRecipientsAvailable !== false,
    );
  }

  builderSummaryRows(): SummaryPanelRow[] {
    return [
      { label: "Stage", value: this.getStageLabel(this.currentStage()) },
      {
        label: "Route",
        value: (() => {
          if (this.hasTransitStop()) {
            return "Transit";
          }
          return "Direct";
        })(),
      },
      { label: "Requests selected", value: this.selectedRequestIds().length },
      { label: "Warehouse", value: this.selectedWarehouseName() },
      { label: "Total owners", value: this.totalOwners() },
      { label: "Total lines", value: this.totalMaterials() },
      { label: "Total requested qty", value: this.totalRequestedQty() },
    ];
  }

  labelPreviewRows(): SummaryPanelRow[] {
    const finalDestination = this.getFinalDestinationSnapshot();

    return [
      {
        label: "Route",
        value: (() => {
          if (this.hasTransitStop()) {
            return "Transit via relay";
          }
          return "Direct";
        })(),
      },
      ...(() => {
        if (this.hasTransitStop()) {
          return [
            { label: "Transit Stop", value: this.selectedTransitPartner?.name || "Not selected", layout: "stacked" as const },
            { label: "Transit Recipient", value: this.selectedTransitRecipient?.name || "Not selected" },
            { label: "Transit Phone", value: this.transitPhoneValue || "-" },
            { label: "Transit Address", value: this.formatAddress(this.transitAddressValue) || "-", layout: "stacked" as const },
          ];
        }
        return [];
      })(),
      { label: "Partner", value: finalDestination?.partnerName || "-" },
      { label: "Recipient", value: finalDestination?.recipientName || "-" },
      { label: "Phone", value: finalDestination?.recipientPhone || "-" },
      { label: "Address", value: this.formatAddress(finalDestination?.destinationAddress) || "-", layout: "stacked" },
    ];
  }

  getFinalDestinationSnapshot(): {
    partnerName: string;
    recipientName: string;
    recipientPhone: string;
    destinationAddress: LocationAddress;
  } | null {
    return buildShipmentDestinationSnapshot({
      selectedPartner: this.selectedFinalPartner,
      selectedRecipient: this.selectedFinalRecipient,
      phoneValue: this.finalPhoneValue,
      addressValue: this.finalAddressValue,
    });
  }

  getTransitSnapshot(): {
    partnerName: string;
    recipientName: string;
    recipientPhone: string;
    destinationAddress: LocationAddress;
  } | null {
    return buildShipmentDestinationSnapshot({
      selectedPartner: this.selectedTransitPartner,
      selectedRecipient: this.selectedTransitRecipient,
      phoneValue: this.transitPhoneValue,
      addressValue: this.transitAddressValue,
    });
  }

  isAddressEmpty(address?: LocationAddress | null): boolean {
    return isShipmentAddressEmpty(address);
  }

  addTransitStop(): void {
    this.hasTransitStop.set(true);
  }

  removeTransitStop(): void {
    this.hasTransitStop.set(false);
    this.resetTransitSelection();
  }

  onTransitAddressChange(value: LocationAddress): void {
    this.transitAddressValue = value;
  }

  async searchTransitPartners(term: string): Promise<void> {
    const response = await this.partnerApi.searchPartners({ search: term, page: 1, limit: 50 });
    response.data.forEach((partner) => {
      this.transitPartnerMap.set(partner.id, partner);
    });

    this.deferViewMutation(() => {
      this.transitPartnerOptions = this.mergeUniqueOptions(
        this.transitPartnerOptions,
        response.data.map((partner) => ({ value: partner.id, label: partner.name })),
      );
    });
  }

  async searchFinalPartners(term: string): Promise<void> {
    const response = await this.partnerApi.searchPartners({ search: term, page: 1, limit: 50 });
    response.data.forEach((partner) => {
      this.finalPartnerMap.set(partner.id, partner);
    });

    this.deferViewMutation(() => {
      this.finalPartnerOptions = this.mergeUniqueOptions(
        this.finalPartnerOptions,
        response.data.map((partner) => ({ value: partner.id, label: partner.name })),
      );
    });
  }

  async onFinalPartnerChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const finalPartnerId = value.toString();
    this.selectedFinalPartnerId = finalPartnerId;
    if (!finalPartnerId) {
      this.resetFinalSelection();
      return;
    }

    this.selectedFinalRecipientId = "";
    this.selectedFinalRecipient = null;
    this.finalRecipientOptions = [];
    this.finalRecipientsAvailable = null;
    this.finalPhoneValue = "";

    const existingPartner = this.finalPartnerMap.get(finalPartnerId);
    if (existingPartner) {
      this.selectedFinalPartner = existingPartner;
      this.finalAddressValue = existingPartner.address || this.createEmptyAddress();
      await this.loadFinalRecipientsForPartner("");
      return;
    }

    const partner = await this.partnerApi.getPartnerById(finalPartnerId);
    if (!partner) {
      this.selectedFinalPartner = null;
      this.toastEffects.show("Final partner not found.", "error");
      return;
    }

    this.finalPartnerMap.set(partner.id, partner);
    this.selectedFinalPartner = partner;
    this.finalAddressValue = partner.address || this.createEmptyAddress();
    this.deferViewMutation(() => {
      this.finalPartnerOptions = this.mergeUniqueOptions(this.finalPartnerOptions, [{ value: partner.id, label: partner.name }]);
    });
    await this.loadFinalRecipientsForPartner("");
  }

  async searchFinalRecipients(term: string): Promise<void> {
    await this.loadFinalRecipientsForPartner(term);
  }

  async onFinalRecipientChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const finalRecipientTeamLeaderId = value.toString();
    this.selectedFinalRecipientId = finalRecipientTeamLeaderId;
    if (!finalRecipientTeamLeaderId) {
      this.selectedFinalRecipient = null;
      this.finalPhoneValue = "";
      return;
    }

    const existingRecipient = this.finalRecipientMap.get(finalRecipientTeamLeaderId);
    if (existingRecipient) {
      this.selectedFinalRecipient = existingRecipient;
      this.finalPhoneValue = existingRecipient.phone || "";
      return;
    }

    const recipient = await this.teamLeaderApi.getTeamLeaderById(finalRecipientTeamLeaderId);
    if (!recipient) {
      this.selectedFinalRecipient = null;
      this.toastEffects.show("Final recipient not found.", "error");
      return;
    }

    this.finalRecipientMap.set(recipient.id, recipient);
    this.selectedFinalRecipient = recipient;
    this.finalPhoneValue = recipient.phone || "";
    this.deferViewMutation(() => {
      this.finalRecipientOptions = this.mergeUniqueOptions(this.finalRecipientOptions, [
        { value: recipient.id, label: recipient.name },
      ]);
    });
  }

  onFinalPhoneChange(value: string): void {
    this.finalPhoneValue = value;
  }

  onFinalAddressChange(value: LocationAddress): void {
    this.finalAddressValue = value;
  }

  async onTransitPartnerChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const transitPartnerId = value.toString();
    this.selectedTransitPartnerId = transitPartnerId;
    if (!transitPartnerId) {
      this.resetTransitSelection();
      return;
    }

    this.selectedTransitRecipientId = "";
    this.selectedTransitRecipient = null;
    this.transitRecipientOptions = [];
    this.transitRecipientsAvailable = null;
    this.transitPhoneValue = "";
    this.transitAddressValue = this.createEmptyAddress();

    const existingPartner = this.transitPartnerMap.get(transitPartnerId);
    if (existingPartner) {
      this.selectedTransitPartner = existingPartner;
      await this.loadTransitRecipientsForPartner("");
      return;
    }

    const partner = await this.partnerApi.getPartnerById(transitPartnerId);
    if (!partner) {
      this.selectedTransitPartner = null;
      this.toastEffects.show("Transit partner not found.", "error");
      return;
    }

    this.transitPartnerMap.set(partner.id, partner);
    this.selectedTransitPartner = partner;
    this.deferViewMutation(() => {
      this.transitPartnerOptions = this.mergeUniqueOptions(this.transitPartnerOptions, [{ value: partner.id, label: partner.name }]);
    });
    await this.loadTransitRecipientsForPartner("");
  }

  async searchTransitRecipients(term: string): Promise<void> {
    await this.loadTransitRecipientsForPartner(term);
  }

  async onTransitRecipientChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const transitRecipientTeamLeaderId = value.toString();
    this.selectedTransitRecipientId = transitRecipientTeamLeaderId;
    if (!transitRecipientTeamLeaderId) {
      this.selectedTransitRecipient = null;
      return;
    }

    const existingRecipient = this.transitRecipientMap.get(transitRecipientTeamLeaderId);
    if (existingRecipient) {
      this.selectedTransitRecipient = existingRecipient;
      this.transitPhoneValue = existingRecipient.phone || "";
      this.transitAddressValue = existingRecipient.address || this.createEmptyAddress();
      return;
    }

    const recipient = await this.teamLeaderApi.getTeamLeaderById(transitRecipientTeamLeaderId);
    if (!recipient) {
      this.selectedTransitRecipient = null;
      this.toastEffects.show("Transit recipient not found.", "error");
      return;
    }

    this.transitRecipientMap.set(recipient.id, recipient);
    this.selectedTransitRecipient = recipient;
    this.transitPhoneValue = recipient.phone || "";
    this.transitAddressValue = recipient.address || this.createEmptyAddress();
    this.deferViewMutation(() => {
      this.transitRecipientOptions = this.mergeUniqueOptions(this.transitRecipientOptions, [
        { value: recipient.id, label: recipient.name },
      ]);
    });
  }

  formatAddress(value?: LocationAddress | null): string {
    return formatShipmentAddress(value);
  }

  toggleRequest(request: RequestEntity): void {
    if (!this.canInteractWithSelection()) {
      return;
    }

    const next = new Set(this.selectedRequestIds());
    if (next.has(request.id)) {
      next.delete(request.id);
    } else {
      const selectedWarehouseId = this.selectedWarehouseId();
      if (selectedWarehouseId && selectedWarehouseId !== request.warehouseId) {
        this.toastEffects.show("Shipment can only group confirmed requests from the same warehouse.", "error");
        return;
      }
      next.add(request.id);
    }

    this.selectedRequestIds.set(Array.from(next));
    this.seedSnapshotFromSelection();
  }

  isRequestSelected(requestId: string): boolean {
    return this.selectedRequestIds().includes(requestId);
  }

  useWarehouseGroup(warehouseId: string): void {
    const selectedGroup = this.warehouseRecoveryGroups().find((group) => group.warehouseId === warehouseId);
    if (!selectedGroup) {
      return;
    }

    this.selectedRequestIds.set(selectedGroup.requests.map((request) => request.id));
    this.prefilledValidationMessage.set("");
    this.warehouseRecoveryGroups.set([]);
    this.seedSnapshotFromSelection(true);
  }

  openStage(stage: ShipmentCreateStage): void {
    if (stage === "snapshot" && !this.canContinueToSnapshot()) {
      this.toastEffects.show("Choose at least one confirmed request first.", "error");
      return;
    }

    if (stage === "review" && !this.canContinueToReview()) {
      this.toastEffects.show("Complete the destination snapshot before reviewing the shipment.", "error");
      return;
    }

    this.currentStage.set(stage);
  }

  goToNextStage(): void {
    if (this.currentStage() === "select") {
      this.openStage("snapshot");
      return;
    }
    if (this.currentStage() === "snapshot") {
      this.openStage("review");
    }
  }

  goToPreviousStage(): void {
    if (this.currentStage() === "review") {
      this.currentStage.set("snapshot");
      return;
    }
    if (this.currentStage() === "snapshot") {
      this.currentStage.set("select");
    }
  }

  async createShipment(): Promise<void> {
    if (this.prefilledValidationMessage()) {
      this.toastEffects.show(this.prefilledValidationMessage(), "error");
      return;
    }

    if (this.isSaving() || !this.canContinueToReview()) {
      this.toastEffects.show("Complete the request selection and destination snapshot first.", "error");
      return;
    }

    this.isSaving.set(true);
    try {
      const created = await this.shipmentEffects.createShipment({
        requestIds: this.selectedRequestIds(),
        finalPartnerId: this.selectedFinalPartnerId,
        finalRecipientTeamLeaderId: this.selectedFinalRecipientId,
        destinationInstitutionName: this.selectedFinalPartner?.name || "",
        recipientName: this.selectedFinalRecipient?.name || "",
        recipientPhone: this.finalPhoneValue,
        destinationAddress: this.finalAddressValue,
        transitPartnerId: (() => {
          if (this.hasTransitStop()) {
            return this.selectedTransitPartnerId;
          }
          return undefined;
        })(),
        transitRecipientTeamLeaderId: (() => {
          if (this.hasTransitStop()) {
            return this.selectedTransitRecipientId;
          }
          return undefined;
        })(),
        transitDestination: (() => {
          if (this.hasTransitStop() && this.getTransitSnapshot()) {
            return {
              transitPartnerId: this.selectedTransitPartnerId,
              transitRecipientTeamLeaderId: this.selectedTransitRecipientId,
              destinationInstitutionName: this.selectedTransitPartner?.name || "",
              recipientName: this.selectedTransitRecipient?.name || "",
              recipientPhone: this.transitPhoneValue,
              destinationAddress: this.transitAddressValue,
            };
          }
          return undefined;
        })(),
        note: this.form.value.note?.trim() ?? "",
      });

      this.toastEffects.show("Shipment created. Continue packing boxes.", "success");
      this.router.navigate(["/shipments/edit", created.id], { queryParams: { stage: "pack" } }).catch(() => undefined);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create shipment."), "error");
    } finally {
      this.isSaving.set(false);
    }
  }

  goBack(): void {
    this.router.navigate(["/shipments"]).catch(() => undefined);
  }

  private async loadConfirmedRequests(): Promise<void> {
    this.isReady.set(false);
    this.prefilledValidationMessage.set("");
    this.warehouseRecoveryGroups.set([]);
    const collected: RequestEntity[] = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await this.requestApi.searchRequests({ status: "Confirmed", page, limit: 100 });
      collected.push(...response.data);
      totalPages = response.pagination.pages || 1;
      page += 1;
    }

    this.requests.set(collected);
    if (this.preselectedRequestIds.length > 0) {
      const validIds = collected.filter((request) => this.preselectedRequestIds.includes(request.id)).map((request) => request.id);
      if (validIds.length === 0) {
        const message = "The selected confirmed requests are no longer available for shipment.";
        this.prefilledValidationMessage.set(message);
        this.toastEffects.show(message, "error");
      } else {
        const selectedRequests = collected.filter((request) => validIds.includes(request.id));
        const warehouseIds = new Set(selectedRequests.map((request) => request.warehouseId).filter(Boolean));
        if (warehouseIds.size > 1) {
          const message =
            "These selected requests come from more than one warehouse. Choose one warehouse group to continue this shipment.";
          this.prefilledValidationMessage.set(message);
          this.warehouseRecoveryGroups.set(this.buildWarehouseRecoveryGroups(selectedRequests));
        }

        this.selectedRequestIds.set(validIds);
        this.seedSnapshotFromSelection();
      }
    }
    this.isReady.set(true);
  }

  private seedSnapshotFromSelection(force = false): void {
    const firstRequest = this.selectedRequests()[0];
    if (!firstRequest) {
      this.resetFinalSelection();
      if (!this.selectedRequestIds().length) {
        this.removeTransitStop();
      }
      return;
    }

    const seededFinal = createSeededDestinationSection(
      {
        partnerId: firstRequest.partnerId,
        partnerName: firstRequest.partner?.name,
        partnerPhone: firstRequest.partner?.phone,
        partnerAddress: firstRequest.partner?.address,
        recipientId: firstRequest.teamLeaderId,
        recipientName: firstRequest.teamLeader?.name,
        recipientPhone: firstRequest.teamLeader?.phone,
        recipientAddress: firstRequest.teamLeader?.address,
      },
      {
        addressSource: "partner",
        phoneSource: "recipient",
      },
    );

    this.selectedFinalPartnerId = seededFinal.selectedPartnerId;
    this.selectedFinalPartner = seededFinal.selectedPartner;
    this.selectedFinalRecipientId = seededFinal.selectedRecipientId;
    this.selectedFinalRecipient = seededFinal.selectedRecipient;
    this.finalPhoneValue = seededFinal.phoneValue;
    this.finalAddressValue = seededFinal.addressValue;
    this.finalRecipientsAvailable = seededFinal.recipientsAvailable;

    if (seededFinal.selectedPartner) {
      this.finalPartnerMap.set(seededFinal.selectedPartnerId, seededFinal.selectedPartner);
      this.finalPartnerOptions = this.mergeUniqueOptions(this.finalPartnerOptions, [
        { value: seededFinal.selectedPartnerId, label: seededFinal.selectedPartner?.name || seededFinal.selectedPartnerId },
      ]);
    }

    if (seededFinal.selectedRecipient) {
      this.finalRecipientMap.set(seededFinal.selectedRecipientId, seededFinal.selectedRecipient);
      this.finalRecipientOptions = this.mergeUniqueOptions(this.finalRecipientOptions, [
        { value: seededFinal.selectedRecipientId, label: seededFinal.selectedRecipient?.name || seededFinal.selectedRecipientId },
      ]);
    } else {
      this.finalRecipientOptions = [];
    }

    if (force && !this.selectedRequestIds().length) {
      this.removeTransitStop();
    }
  }

  private async loadFinalRecipientsForPartner(term: string): Promise<void> {
    if (!this.selectedFinalPartnerId) {
      this.deferViewMutation(() => {
        this.finalRecipientOptions = [];
        this.finalRecipientsAvailable = null;
      });
      return;
    }

    const response = await this.teamLeaderApi.searchTeamLeaders({
      search: term,
      partnerIds: [this.selectedFinalPartnerId],
      page: 1,
      limit: 50,
    });

    response.data.forEach((teamLeader) => {
      this.finalRecipientMap.set(teamLeader.id, teamLeader);
    });

    this.deferViewMutation(() => {
      this.finalRecipientOptions = this.mergeUniqueOptions(
        this.finalRecipientOptions,
        response.data.map((teamLeader) => ({ value: teamLeader.id, label: teamLeader.name })),
      );
      this.finalRecipientsAvailable = response.data.length > 0;
    });
  }

  private async loadTransitRecipientsForPartner(term: string): Promise<void> {
    if (!this.selectedTransitPartnerId) {
      this.deferViewMutation(() => {
        this.transitRecipientOptions = [];
        this.transitRecipientsAvailable = null;
      });
      return;
    }

    const response = await this.teamLeaderApi.searchTeamLeaders({
      search: term,
      partnerIds: [this.selectedTransitPartnerId],
      page: 1,
      limit: 50,
    });

    response.data.forEach((teamLeader) => {
      this.transitRecipientMap.set(teamLeader.id, teamLeader);
    });

    this.deferViewMutation(() => {
      this.transitRecipientOptions = this.mergeUniqueOptions(
        this.transitRecipientOptions,
        response.data.map((teamLeader) => ({ value: teamLeader.id, label: teamLeader.name })),
      );
      this.transitRecipientsAvailable = response.data.length > 0;
    });
  }

  private resetTransitSelection(): void {
    const emptyTransit = createEmptyDestinationSection();
    this.selectedTransitPartnerId = emptyTransit.selectedPartnerId;
    this.selectedTransitPartner = emptyTransit.selectedPartner;
    this.selectedTransitRecipientId = emptyTransit.selectedRecipientId;
    this.selectedTransitRecipient = emptyTransit.selectedRecipient;
    this.transitPhoneValue = emptyTransit.phoneValue;
    this.transitRecipientOptions = [];
    this.transitRecipientsAvailable = emptyTransit.recipientsAvailable;
    this.transitAddressValue = emptyTransit.addressValue;
  }

  private resetFinalSelection(): void {
    const emptyFinal = createEmptyDestinationSection();
    this.selectedFinalPartnerId = emptyFinal.selectedPartnerId;
    this.selectedFinalPartner = emptyFinal.selectedPartner;
    this.selectedFinalRecipientId = emptyFinal.selectedRecipientId;
    this.selectedFinalRecipient = emptyFinal.selectedRecipient;
    this.finalRecipientOptions = [];
    this.finalRecipientsAvailable = emptyFinal.recipientsAvailable;
    this.finalPhoneValue = emptyFinal.phoneValue;
    this.finalAddressValue = emptyFinal.addressValue;
  }

  private buildWarehouseRecoveryGroups(requests: RequestEntity[]): ShipmentWarehouseGroup[] {
    const grouped = new Map<string, ShipmentWarehouseGroup>();

    for (const request of requests) {
      const warehouseId = request.warehouseId || "unknown";
      const current =
        grouped.get(warehouseId) ??
        ({
          warehouseId,
          warehouseName: request.warehouse?.name || "Unknown Warehouse",
          requests: [],
          requestCount: 0,
          totalRequestedQty: 0,
        } satisfies ShipmentWarehouseGroup);

      current.requests.push(request);
      current.requestCount += 1;
      current.totalRequestedQty += request.items.reduce((sum, item) => sum + Number(item.qty ?? 0), 0);
      grouped.set(warehouseId, current);
    }

    return Array.from(grouped.values()).sort((left, right) => right.requestCount - left.requestCount);
  }

  private createEmptyAddress(): LocationAddress {
    return createEmptyShipmentAddress();
  }

  private mergeUniqueOptions(base: AutocompleteOption[], incoming: AutocompleteOption[]): AutocompleteOption[] {
    const map = new Map<string | number, AutocompleteOption>();
    [...base, ...incoming].forEach((option) => {
      if (!option.value) {
        return;
      }
      map.set(option.value, option);
    });

    return Array.from(map.values());
  }

  private deferViewMutation(callback: () => void): void {
    setTimeout(() => {
      callback();
      this.cdr.detectChanges();
    }, 0);
  }
}
